import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { hasAnalyticsConsent } from '@/utils/cookieConsent';

const GTM_ID = 'GTM-55CSLPKR';
const DEBUG_MODE = process.env.NODE_ENV === 'development';

declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
    gtmLoaded?: boolean;
  }
}

// Initialize dataLayer
if (typeof window !== 'undefined') {
  window.dataLayer = window.dataLayer || [];
  window.gtag = function() {
    window.dataLayer.push(arguments);
  };
}

const GoogleTagManager = () => {
  const location = useLocation();
  const scriptLoadedRef = useRef(false);

  useEffect(() => {
    // Only load GTM if user has consented to analytics
    if (!hasAnalyticsConsent()) {
      if (DEBUG_MODE) console.log('GTM: No analytics consent, skipping script load');
      return;
    }

    // Prevent duplicate script loading
    if (scriptLoadedRef.current || window.gtmLoaded) {
      if (DEBUG_MODE) console.log('GTM: Script already loaded, skipping');
      return;
    }

    // Check if script already exists
    const existingScript = document.querySelector(`script[src*="googletagmanager.com/gtm.js?id=${GTM_ID}"]`);
    if (existingScript) {
      if (DEBUG_MODE) console.log('GTM: Script element already exists');
      scriptLoadedRef.current = true;
      window.gtmLoaded = true;
      return;
    }

    if (DEBUG_MODE) console.log('GTM: Loading script for container', GTM_ID);

    // Load GTM script
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`;
    
    script.onload = () => {
      if (DEBUG_MODE) console.log('GTM: Script loaded successfully');
      scriptLoadedRef.current = true;
      window.gtmLoaded = true;
    };

    script.onerror = () => {
      console.error('GTM: Failed to load script');
    };

    document.head.appendChild(script);
  }, []);

  // Track page views on route change
  useEffect(() => {
    if (!hasAnalyticsConsent()) {
      if (DEBUG_MODE) console.log('GTM: No analytics consent, skipping page view');
      return;
    }

    if (window.dataLayer) {
      const pageData = {
        event: 'page_view',
        page_title: document.title,
        page_location: window.location.href,
        page_path: location.pathname + location.search,
        app_version: '2.0',
        layout_version: 'interactive_preview'
      };

      window.dataLayer.push(pageData);
      
      if (DEBUG_MODE) {
        console.log('GTM: Page view tracked', pageData);
      }
    } else if (DEBUG_MODE) {
      console.log('GTM: DataLayer not available for page view tracking');
    }
  }, [location]);

  return null;
};

// Enhanced tracking functions for GTM with Google Ads optimization
export const trackGTMEvent = (eventName: string, parameters?: Record<string, any>) => {
  if (window.dataLayer) {
    const eventData = {
      event: eventName,
      event_category: 'engagement',
      event_label: eventName,
      app_version: '2.0',
      layout_version: 'interactive_preview',
      ...parameters,
    };
    
    window.dataLayer.push(eventData);
    if (DEBUG_MODE) console.log('GTM: Event tracked', eventName, eventData);
  } else if (DEBUG_MODE) {
    console.log('GTM: DataLayer not available, event queued:', eventName, parameters);
  }
};

export const trackGTMConversion = (eventName: string, value?: number, conversionId?: string) => {
  if (!hasAnalyticsConsent()) {
    if (DEBUG_MODE) console.log('GTM: No analytics consent, skipping conversion', eventName);
    return;
  }
  
  if (window.dataLayer) {
    const conversionData = {
      event: 'conversion',
      event_category: 'conversion',  
      event_label: eventName,
      app_version: '2.0',
      layout_version: 'interactive_preview',
      value: value || 0,
      currency: 'BRL',
      conversion_id: conversionId,
      // Google Ads Enhanced Conversions data
      enhanced_conversion_data: {
        email: undefined,
        phone_number: undefined
      }
    };
    
    window.dataLayer.push(conversionData);
    if (DEBUG_MODE) console.log('GTM: Conversion tracked', eventName, conversionData);
  } else if (DEBUG_MODE) {
    console.log('GTM: DataLayer not available, conversion queued:', eventName, value);
  }
};

export const trackGTMSubscription = (email: string, plan?: string) => {
  if (!hasAnalyticsConsent()) {
    if (DEBUG_MODE) console.log('GTM: No analytics consent, skipping subscription', email);
    return;
  }
  
  if (window.dataLayer) {
    const subscriptionData = {
      event: 'newsletter_signup',
      event_category: 'conversion',
      event_label: 'newsletter_subscription',
      user_email: email,
      subscription_plan: plan || 'trial',
      app_version: '2.0',
      layout_version: 'interactive_preview',
      value: 10, // Assign a value for Google Ads optimization
      currency: 'BRL',
      // Enhanced Conversions for better tracking
      user_data: {
        email_address: email,
        subscription_status: 'active'
      }
    };
    
    window.dataLayer.push(subscriptionData);
    if (DEBUG_MODE) console.log('GTM: Subscription tracked', email, subscriptionData);
  } else if (DEBUG_MODE) {
    console.log('GTM: DataLayer not available, subscription queued:', email, plan);
  }
};

// Enhanced funnel tracking for Google Ads optimization
export const trackGTMConversionFunnel = (step: string, stepNumber: number, value?: number) => {
  if (!hasAnalyticsConsent()) {
    if (DEBUG_MODE) console.log('GTM: No analytics consent, skipping funnel', step);
    return;
  }
  
  if (window.dataLayer) {
    const funnelData = {
      event: 'conversion_funnel_step',
      event_category: 'funnel',
      funnel_step: step,
      step_number: stepNumber,
      app_version: '2.0',
      funnel_type: 'newsletter_signup_v2',
      value: value || (stepNumber === 4 ? 10 : stepNumber * 2), // Progressive value assignment
      currency: 'BRL'
    };
    
    window.dataLayer.push(funnelData);
    if (DEBUG_MODE) console.log('GTM: Funnel step tracked', step, funnelData);
  } else if (DEBUG_MODE) {
    console.log('GTM: DataLayer not available, funnel step queued:', step, stepNumber);
  }
};

export const trackGTMFormEngagement = (formType: string, engagement: string, fieldName?: string) => {
  if (window.dataLayer) {
    const engagementData = {
      event: 'form_engagement',
      event_category: 'form_interaction',
      form_type: formType,
      engagement_type: engagement,
      field_name: fieldName,
      app_version: '2.0',
      value: engagement === 'submit' ? 5 : 1 // Higher value for submissions
    };
    
    window.dataLayer.push(engagementData);
    if (DEBUG_MODE) console.log('GTM: Form engagement tracked', formType, engagementData);
  } else if (DEBUG_MODE) {
    console.log('GTM: DataLayer not available, form engagement queued:', formType, engagement);
  }
};

// Interactive preview tracking with Google Ads parameters
export const trackGTMInteractivePreview = (action: string, templateId?: string) => {
  if (window.dataLayer) {
    const previewData = {
      event: 'interactive_preview_engagement',
      event_category: 'preview_interaction',
      action: action,
      template_id: templateId,
      app_version: '2.0',
      engagement_type: 'preview_interaction',
      value: 3 // Assign value for engagement tracking
    };
    
    window.dataLayer.push(previewData);
    if (DEBUG_MODE) console.log('GTM: Interactive preview tracked', action, previewData);
  } else if (DEBUG_MODE) {
    console.log('GTM: DataLayer not available, preview interaction queued:', action, templateId);
  }
};

// Ecommerce events for enhanced conversion tracking
export const trackGTMBeginCheckout = (email?: string) => {
  if (window.dataLayer) {
    const checkoutData = {
      event: 'begin_checkout',
      event_category: 'ecommerce',
      value: 10,
      currency: 'BRL',
      items: [{
        item_id: 'newsletter_subscription',
        item_name: 'Newsletter Subscription',
        item_category: 'subscription',
        price: 10,
        quantity: 1
      }],
      user_data: email ? { email_address: email } : undefined
    };
    
    window.dataLayer.push(checkoutData);
    if (DEBUG_MODE) console.log('GTM: Begin checkout tracked', checkoutData);
  }
};

export const trackGTMPurchase = (email: string, plan: string = 'trial', value?: number, currency?: string) => {
  if (window.dataLayer) {
    const defaultValue = value || 10;
    const defaultCurrency = currency || 'BRL';
    
    const purchaseData = {
      event: 'purchase',
      event_category: 'ecommerce',
      transaction_id: `newsletter_${Date.now()}`,
      value: defaultValue,
      currency: defaultCurrency,
      items: [{
        item_id: `newsletter_${plan}`,
        item_name: `Newsletter ${plan.charAt(0).toUpperCase() + plan.slice(1)}`,
        item_category: 'subscription',
        price: defaultValue,
        quantity: 1
      }],
      user_data: {
        email_address: email,
        subscription_plan: plan
      }
    };
    
    window.dataLayer.push(purchaseData);
    if (DEBUG_MODE) console.log('GTM: Purchase tracked', purchaseData);
  }
};

export const trackGTMCheckoutRenewal = (additionalParams?: Record<string, any>) => {
  if (!hasAnalyticsConsent()) {
    if (DEBUG_MODE) console.log('GTM: No analytics consent, skipping renewal');
    return;
  }
  
  if (window.dataLayer) {
    const renewalData = {
      event: 'checkout_timer_renewed',
      event_category: 'Checkout',
      event_label: 'Timer Renewal',
      app_version: '2.0',
      ...additionalParams
    };
    
    window.dataLayer.push(renewalData);
    if (DEBUG_MODE) console.log('GTM: Checkout renewal tracked', renewalData);
  }
};

export default GoogleTagManager;